/**
 * STAND-IN (STAND-IN.md) — MY stand-in in this room: the setting, the agents I may choose (those I supervise here),
 * my pending drafts, what it learned from me, its track record. Re-read on room switch / reconnect / focus and when a
 * STANDIN_UPDATED beacon names ME (the beacon carries ids only — the drafts come from the auth-checked GET).
 * ``thinking`` = questionIds a stand-in (anyone's) is drafting for right now → "Architect is answering for Ana…".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getStandin, standinOp } from '@stepflow/ui-shared/services/dataPlaneApi';

const EMPTY = { setting: { enabled: false, agentId: '', threshold: 0.8 }, eligible: [], drafts: [], lessons: [],
  trackRecord: { reviewed: 0, acceptedRate: null }, loaded: false };

export function standinBeacons(messages, seen, me) {
  const out = { mine: false, thinking: [], done: [] };
  for (const m of messages || []) {
    if (!m || (m.eventType ?? m.type) !== 'STANDIN_UPDATED') continue;
    const id = String(m.eventId || '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const p = m.payload || {};
    if (p.forSub && p.forSub === me) out.mine = true;
    if (p.state === 'thinking' && p.questionId) out.thinking.push({ questionId: p.questionId, agentId: p.agentId || '', forSub: p.forSub || '' });
    else if (p.questionId) out.done.push(p.questionId);
  }
  return out;
}

export default function useStandin(networkId, poolId, me, messages, reconnectEpoch = 0) {
  const [state, setState] = useState(EMPTY);
  const [thinking, setThinking] = useState({});           // questionId → {agentId, forSub}
  const roomRef = useRef('');
  const room = `${networkId}/${poolId}`;
  roomRef.current = room;
  const load = useCallback(() => {
    if (!networkId || !me) return;
    const key = `${networkId}/${poolId}`;
    getStandin(networkId, { pool: poolId })
      .then((r) => { if (roomRef.current === key) setState({ ...EMPTY, ...(r || {}), loaded: true }); })
      .catch(() => { if (roomRef.current === key) setState((s) => ({ ...s, loaded: true })); });
  }, [networkId, poolId, me]);
  useEffect(() => { setState(EMPTY); setThinking({}); }, [networkId, poolId]);
  useEffect(() => { load(); }, [load, reconnectEpoch]);
  useEffect(() => {
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [load]);
  const seenRef = useRef(new Set());
  useEffect(() => {
    const b = standinBeacons(messages, seenRef.current, me);
    if (b.thinking.length || b.done.length) {
      setThinking((t) => {
        const n = { ...t };
        for (const x of b.thinking) n[x.questionId] = x;
        for (const q of b.done) delete n[q];
        return n;
      });
    }
    if (b.mine) load();
  }, [messages, me, load]);

  const run = useCallback(async (op, payload) => {
    const r = await standinOp(networkId, op, payload, { pool: poolId });
    load();
    return r;
  }, [networkId, poolId, load]);
  return { ...state, thinking, reload: load, run };
}
